import { Box, Progress as ChakraProgress, Flex, Stack, useBreakpointValue } from '@chakra-ui/react';
import React, { useMemo } from 'react';

import { noop } from '@blockpulse3/data/shared';

import { Progress, ProgressStep, StepState } from './Progress';
import { STEP_HEADER_HEIGHT_BASE, STEP_HEADER_HEIGHT_MD, Step, StepProps } from './Step';

type Props = {
  /* ** Display progress bar ** */
  showProgress?: boolean;
  /* ** Current step value (0 based) ** */
  currentStep: number;
  /* ** Flex basis offset ** */
  offset?: number;
  /* ** On step progress click ** */
  onStepClick?: (index: number, stepState: StepState) => void;
  /* ** Steps node ** */
  children?: React.ReactElement<StepProps> | React.ReactElement<StepProps>[];
};

/**
 * Wizard.
 *
 * Wizard component used with <Step /> as children.
 * It loops throught step titles to render the active
 * one.
 *
 * @param {Props}
 * @returns {JSX.Element}
 */
export function Wizard({
  showProgress = true,
  offset = 200,
  currentStep,
  onStepClick = noop,
  children,
}: Props): JSX.Element {
  const top = useBreakpointValue({ base: STEP_HEADER_HEIGHT_BASE, md: STEP_HEADER_HEIGHT_MD });

  /* Get all titles from steps to render <Progress /> */
  const stepTitles = useMemo(() => {
    if (!children) {
      return [];
    }
    return React.Children.map(children, (child) => child.props.title);
  }, [children]);

  const handleStepClick = (index: number, stepState: StepState): void => {
    onStepClick(index, stepState);
  };

  return (
    <Box>
      {showProgress && (
        <ChakraProgress
          height={1}
          max={stepTitles.length}
          position="sticky"
          top={top}
          value={currentStep}
          zIndex="2"
          sx={{
            '&>div': {
              transition: 'width .7s ease',
            },
          }}
        />
      )}
      <Stack direction={{ base: 'column', lg: 'row' }} p="6" spacing={{ base: 4, lg: 0 }}>
        <Flex flexBasis={{ base: 0, lg: offset }} />
        <Progress>
          {stepTitles.map((step, idx) => (
            <ProgressStep
              key={step}
              index={idx}
              isActive={currentStep === idx}
              isCompleted={currentStep > idx}
              title={step}
              onClick={handleStepClick}
            />
          ))}
        </Progress>
        <Flex flexBasis="45%" flexGrow="1" maxW="3xl">
          {React.Children.map(
            children,
            (child, idx) => child && <Step {...child.props} isActive={currentStep === idx} />,
          )}
        </Flex>
      </Stack>
    </Box>
  );
}

export type WizardProps = Props;
